import { Box, Button, Checkbox, Divider, FormControlLabel, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { SelectField } from "../../../../components/Form/SelectField";
import { InputField } from "../../../../components/Form/InputField";
import SelectFieldWithSearch from "../../../../components/Form/SelectFieldWithSearch";
import { generateCampaignCodes } from "../../../../service/BuyerConfiguration/CampaignManagement/campaignService";
import { notifyError, notifySuccess } from "../../../../components/ToastContainer/Toast/Toast";
import { getStore } from "../../../../service/store/StoreService";
import { categoryList } from "../../../../service/products/ProductServices";

export const COUPON_TYPE_OPTIONS = [
  { label: "Percent", value: "percent" },
  { label: "Flat", value: "flat" },
  { label: "First Order", value: "first_order" },
  { label: "Preorder", value: "preorder" },
  { label: "Free Delivery", value: "free_delivery" },
] as const;

type PropsGenerateCouponsModal = {
  campaignId?: string;
  onClose: () => void;
  /** Called after coupons are generated successfully, before the modal closes */
  onGenerated?: () => void;
};

type PercentFormValues = {
  coupon_type: string;
  discount_type: "" | "rupees" | "percent";
  delivery_type: string;
  prefix: string;
  length: string;
  value: string;
  max_discount_amount: string;
  min_cart_value: string;
  start_at: Dayjs | null;
  expires_at: Dayjs | null;
  user_usage_limit: string;
  global_usage_limit: string;
  store_reference_id: string;
  item_reference_ids: string[];
  delivery_fee_cap: string;
  free_delivery: boolean;
  order_coupon_count: string;
  preorder_title: string;
  preorder_delivery_date: Dayjs | null;
  preorder_item_id: string;
};

const isStandardType = (couponType?: string) =>
  couponType === "percent" || couponType === "flat";
const isStandardOrFirstOrderType = (couponType?: string) =>
  couponType === "percent" || couponType === "flat" || couponType === "first_order";
const isPreorderType = (couponType?: string) => couponType === "preorder";

const toNullableTrimmedString = (v: unknown): string | null => {
  if (typeof v !== "string") return v === null || v === undefined ? null : String(v);
  const trimmed = v.trim();
  return trimmed ? trimmed : null;
};

const toNullableNumber = (v: unknown): number | null => {
  if (v === null || v === undefined) return null;
  if (typeof v === "string") {
    const trimmed = v.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toNullableIsoDayStart = (d: Dayjs | null): string | null =>
  d ? d.startOf("day").format('YYYY-MM-DDTHH:mm:ss.SSSZ') : null;
const toNullableIsoDayEnd = (d: Dayjs | null): string | null =>
  d ? d.endOf("day").format('YYYY-MM-DDTHH:mm:ss.SSSZ') : null;

const numberString = (fieldLabel: string) =>
  Yup.string()
    .transform((v) => (typeof v === "string" ? v.trim() : v))
    .matches(/^\d*$/, `${fieldLabel} must be a number`);

const percentSchema = Yup.object({
  coupon_type: Yup.string().oneOf(COUPON_TYPE_OPTIONS.map((o) => o.value)).required("Coupon Type is required"),
  discount_type: Yup.string().trim().when("coupon_type", {
    is: (v: string) => v === "first_order" || v === "preorder" || v === "free_delivery",
    then: (schema) =>
      schema.oneOf(["rupees", "percent"]).required("Discount Type is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  delivery_type: Yup.string().trim().notRequired(),
  prefix: Yup.string().trim().when("coupon_type", {
    is: (v: string) => isStandardOrFirstOrderType(v) || isPreorderType(v),
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.notRequired(),
  }),
  length: numberString("Length")
    .when("coupon_type", {
      is: (v: string) => isStandardOrFirstOrderType(v) || isPreorderType(v) || v === "free_delivery",
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.notRequired(),
    })
    .test("length-range", "Length must be between 6 and 8", (v) => {
      if (!v) return true;
      const n = Number(v);
      return Number.isFinite(n) && n >= 6 && n <= 8;
    }),
  value: numberString("Value").when("coupon_type", {
    is: (v: string) => isStandardOrFirstOrderType(v) || isPreorderType(v),
    then: (schema) => schema.required("Value is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  max_discount_amount: numberString("Maximum Discount Amount").when("coupon_type", {
    is: (v: string) => isStandardOrFirstOrderType(v) || isPreorderType(v) || v === "free_delivery",
    then: (schema) =>
      schema
        .required("Value must be at least 1")
        .test("min-1", "Value must be at least 1", (v) => {
          if (!v) return false;
          const n = Number(v);
          return Number.isFinite(n) && n >= 1;
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  min_cart_value: numberString("Minimum Cart Value").when("coupon_type", {
    is: (v: string) => isStandardOrFirstOrderType(v) || isPreorderType(v),
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.notRequired(),
  }),
  start_at: Yup.mixed<Dayjs | null>()
    .nullable()
    .notRequired()
    .test("no-future", "Start Date should not be in the future", (v) => {
      if (!v) return true;
      return !v.isAfter(dayjs(), "day");
    }),
  expires_at: Yup.mixed<Dayjs | null>()
    .nullable()
    .when("coupon_type", {
      is: "free_delivery",
      then: (schema) => schema.required("Expiry Date is required"),
      otherwise: (schema) => schema.notRequired(),
    })
    .test("no-past", "Expiry Date should not be in the past", (v) => {
      if (!v) return true;
      return !v.isBefore(dayjs(), "day");
    })
    .test("after-start", "Expiry Date must be on or after Start Date", function (v) {
      const start = this.parent?.start_at as Dayjs | null;
      if (!v || !start) return true;
      return !v.isBefore(start, "day");
    }),
  user_usage_limit: numberString("Per User Limit").when("coupon_type", {
    is: (v: string) => isStandardOrFirstOrderType(v) || isPreorderType(v),
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.notRequired(),
  }),
  global_usage_limit: numberString("Overall Usage Limit").when("coupon_type", {
    is: "preorder",
    then: (schema) => schema.required("Overall Usage Limit is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  store_reference_id: Yup.string().trim().when("coupon_type", {
    is: "preorder",
    then: (schema) => schema.required("Select Store is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  item_reference_ids: Yup.array().of(Yup.string().trim()).when("coupon_type", {
    is: (_v: string) => false,
    then: (schema) => schema,
    otherwise: (schema) => schema.notRequired(),
  }),
  delivery_fee_cap: numberString("Delivery Fee Cap").notRequired(),
  free_delivery: Yup.boolean().notRequired(),
  order_coupon_count: numberString("Order Coupon Count").when("coupon_type", {
    is: "first_order",
    then: (schema) =>
      schema
        .required("Enter Applicabale value")
        .test("min-1", "Order Coupon Count must not be less than 1", (v) => {
          if (!v) return false;
          const n = Number(v);
          return Number.isFinite(n) && n >= 1;
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  preorder_title: Yup.string().trim().when("coupon_type", {
    is: "preorder",
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.notRequired(),
  }),
  preorder_item_id: Yup.string().trim().when("coupon_type", {
    is: "preorder",
    then: (schema) => schema.required("Select Product is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  preorder_delivery_date: Yup.mixed<Dayjs | null>()
    .nullable()
    .when("coupon_type", {
      is: "preorder",
      then: (schema) => schema.required("Delivery Date is required"),
      otherwise: (schema) => schema.notRequired(),
    })
    .test("future-delivery", "Delivery Date must be a future date", (v) => {
      if (!v) return true;
      return v.isAfter(dayjs(), "day");
    }),
});

const GenerateCouponsModal: React.FC<PropsGenerateCouponsModal> = ({ onClose, campaignId, onGenerated }) => {
  const [storeOptions, setStoreOptions] = useState<{ label: string; value: string }[]>([]);
  const [productOptions, setProductOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const res = await getStore(1, 150);
        const stores = res?.data?.data?.stores || [];
        const mapped = (Array.isArray(stores) ? stores : []).map((s: any) => ({
          value: String(s?.id ?? ""),
          label: String(s?.name ?? ""),
        })).filter((o: any) => o.value && o.label);
        setStoreOptions(mapped);
      } catch (e: any) {
        notifyError(e?.response?.data?.message || "Failed to load stores");
      }
    };

    fetchStores();
  }, []);

  const fetchProductsForStore = async (storeId: string) => {
    if (!storeId) {
      setProductOptions([]);
      return;
    }
    try {
      const res = await categoryList(storeId);
      const categories = res?.data?.data || [];

      const productMap = new Map<string, string>();
      (Array.isArray(categories) ? categories : []).forEach((cat: any) => {
        const children = cat?.children || [];
        (Array.isArray(children) ? children : []).forEach((child: any) => {
          const products = child?.products || [];
          (Array.isArray(products) ? products : []).forEach((p: any) => {
            const id = p?.id;
            const name = p?.name;
            if (id !== undefined && id !== null && name) {
              productMap.set(String(id), String(name));
            }
          });
        });
      });

      const mapped = Array.from(productMap.entries()).map(([value, label]) => ({ value, label }));
      setProductOptions(mapped);
    } catch (e: any) {
      setProductOptions([]);
      notifyError(e?.response?.data?.message || "Failed to load products");
    }
  };

  return (
    <Formik
      initialValues={{
        coupon_type: "",
        discount_type: "",
        delivery_type: "",
        prefix: "",
        length: "",
        value: "",
        max_discount_amount: "",
        min_cart_value: "",
        start_at: null,
        expires_at: null,
        user_usage_limit: "",
        global_usage_limit: "",
        store_reference_id: "",
        item_reference_ids: [],
        delivery_fee_cap: "",
        free_delivery: false,
        order_coupon_count: "",
        preorder_title: "",
        preorder_delivery_date: null,
        preorder_item_id: "",
      } as PercentFormValues}
      validationSchema={percentSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          if (!campaignId) {
            notifyError("Campaign id is missing");
            return;
          }
          const startAtIso = toNullableIsoDayStart(values.start_at);
          const expiresAtIso = toNullableIsoDayEnd(values.expires_at);

          let payload: any = null;

          if (isStandardType(values.coupon_type)) {
            const storeReferenceId = toNullableTrimmedString(values.store_reference_id);
            const deliveryFeeCap = toNullableNumber(values.delivery_fee_cap);
            payload = {
              count: 1,
              prefix: toNullableTrimmedString(values.prefix),
              separator: "",
              length: toNullableNumber(values.length),
              type: values.coupon_type,
              value: Number(values.value),
              value_type: values.coupon_type === "flat" ? "rupees" : values.coupon_type,
              max_discount_amount: toNullableNumber(values.max_discount_amount),
              min_cart_value: toNullableNumber(values.min_cart_value),
              expires_at: expiresAtIso,
              start_at: startAtIso,
              user_usage_limit: toNullableNumber(values.user_usage_limit),
              global_usage_limit: toNullableNumber(values.global_usage_limit),
              preview: false,
              type_meta: {
                free_delivery: Boolean(values.free_delivery),
                ...(values.free_delivery && deliveryFeeCap !== null
                  ? { delivery_fee_cap: deliveryFeeCap }
                  : {}),
                ...(storeReferenceId ? { store_reference_id: storeReferenceId } : {}),
                item_reference_ids: Array.isArray(values.item_reference_ids)
                  ? values.item_reference_ids
                  : [],
              },
            };
          } else if (values.coupon_type === "first_order") {
            const deliveryFeeCap = toNullableNumber(values.delivery_fee_cap);
            payload = {
              count: 1,
              prefix: toNullableTrimmedString(values.prefix),
              separator: "",
              length: toNullableNumber(values.length),
              type: "nth_order",
              value: Number(values.value),
              value_type: values.discount_type,
              max_discount_amount: toNullableNumber(values.max_discount_amount),
              min_cart_value: toNullableNumber(values.min_cart_value),
              start_at: startAtIso,
              expires_at: expiresAtIso,
              user_usage_limit: toNullableNumber(values.user_usage_limit),
              global_usage_limit: toNullableNumber(values.global_usage_limit),
              preview: false,
              type_meta: {
                nth: toNullableNumber(values.order_coupon_count),
                free_delivery: Boolean(values.free_delivery),
                ...(values.free_delivery && deliveryFeeCap !== null
                  ? { delivery_fee_cap: deliveryFeeCap }
                  : {}),
              },
            };
          } else if (values.coupon_type === "free_delivery") {
            const storeReferenceId = toNullableTrimmedString(values.store_reference_id);
            payload = {
              count: 1,
              prefix: toNullableTrimmedString(values.prefix),
              separator: "",
              length: toNullableNumber(values.length),
              type: "free_delivery",
              value: 0,
              value_type: values.discount_type,
              max_discount_amount: toNullableNumber(values.max_discount_amount),
              min_cart_value: toNullableNumber(values.min_cart_value),
              expires_at: expiresAtIso,
              start_at: startAtIso,
              user_usage_limit: toNullableNumber(values.user_usage_limit),
              global_usage_limit: toNullableNumber(values.global_usage_limit),
              preview: false,
              type_meta: {
                ...(storeReferenceId ? { store_reference_id: storeReferenceId } : {}),
                item_reference_ids: Array.isArray(values.item_reference_ids)
                  ? values.item_reference_ids
                  : [],
              },
            };
          } else if (values.coupon_type === "preorder") {
            const deliveryDateIso = values.preorder_delivery_date
              ? values.preorder_delivery_date.format("YYYY-MM-DD")
              : null;
            const storeReferenceId = toNullableTrimmedString(values.store_reference_id);
            const deliveryFeeCap = toNullableNumber(values.delivery_fee_cap);
            payload = {
              count: 1,
              prefix: toNullableTrimmedString(values.prefix),
              separator: "",
              length: toNullableNumber(values.length),
              type: "preorder",
              value: Number(values.value),
              value_type: values.discount_type,
              max_discount_amount: toNullableNumber(values.max_discount_amount),
              min_cart_value: toNullableNumber(values.min_cart_value),
              expires_at: expiresAtIso,
              start_at: startAtIso,
              user_usage_limit: toNullableNumber(values.user_usage_limit),
              global_usage_limit: toNullableNumber(values.global_usage_limit),
              priority: 5,
              preview: false,
              type_meta: {
                item_reference_id: toNullableTrimmedString(values.preorder_item_id),
                title: toNullableTrimmedString(values.preorder_title),
                delivery_date: deliveryDateIso,
                free_delivery: Boolean(values.free_delivery),
                ...(values.free_delivery && deliveryFeeCap !== null
                  ? { delivery_fee_cap: deliveryFeeCap }
                  : {}),
                ...(storeReferenceId ? { store_reference_id: storeReferenceId } : {}),
              },
            };
          } else {
            notifyError("This coupon type is not configured yet");
            return;
          }

          await generateCampaignCodes(campaignId, payload);
          notifySuccess("Coupons generated successfully");
          onGenerated?.();
          onClose();
        } catch (error: any) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to generate coupons";
          notifyError(Array.isArray(message) ? message[0] : message);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, isSubmitting, setFieldValue, errors, touched }) => {
        const showPercentFields = isStandardType(values.coupon_type);
        const showFirstOrderFields = values.coupon_type === "first_order";
        const showPreorderFields = values.coupon_type === "preorder";
        const showFreeDeliveryTypeFields = values.coupon_type === "free_delivery";

        const showFieldError = (name: keyof PercentFormValues) =>
          Boolean((touched as any)?.[name] && (errors as any)?.[name]);

        return (
        <Form>
          <Grid container>
            <Grid item xs={12}>
              <SelectField
                label="Coupon Type"
                name="coupon_type"
                value={values.coupon_type}
                options={[...COUPON_TYPE_OPTIONS]}
                isRequired
              />
            </Grid>
          </Grid>

          <Divider sx={{ mt: 2, mb: 2 }} />

          {showPercentFields || showFirstOrderFields || showPreorderFields || showFreeDeliveryTypeFields ? (
            <>
              <Grid container>
                {showPercentFields ? (
                  <>
                    {/* Row 1: Prefix — Length — Value */}
                    <Grid item xs={12} md={4} sx={{ mt: 1 }}>
                      <InputField
                        label="Prefix"
                        name="prefix"
                        placeholder="ITEM20"
                        value={values.prefix}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 1, pl: { md: 2 } }}>
                      <InputField
                        label="Length"
                        name="length"
                        placeholder="8"
                        value={values.length}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) => {
                          const onlyDigits = e.target.value.replace(/\D/g, "");
                          setFieldValue("length", onlyDigits);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 1, pl: { md: 2 } }}>
                      <InputField
                        label="Value"
                        name="value"
                        placeholder="20"
                        value={values.value}
                        isRequired
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) => setFieldValue("value", e.target.value.replace(/\D/g, ""))}
                      />
                    </Grid>

                    {/* Row 2: Per User Limit — Overall Usage Limit — Max Discount Amount */}
                    <Grid item xs={12} md={4} sx={{ mt: 2 }}>
                      <InputField
                        label="Per User Limit"
                        name="user_usage_limit"
                        placeholder="eg: 1"
                        value={values.user_usage_limit}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) =>
                          setFieldValue("user_usage_limit", e.target.value.replace(/\D/g, ""))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 2, pl: { md: 2 } }}>
                      <InputField
                        label="Overall Usage Limit"
                        name="global_usage_limit"
                        placeholder="eg: 300"
                        value={values.global_usage_limit}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) =>
                          setFieldValue("global_usage_limit", e.target.value.replace(/\D/g, ""))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 2, pl: { md: 2 } }}>
                      <InputField
                        label="Max Discount Value"
                        name="max_discount_amount"
                        placeholder="Enter Max Discount Value"
                        value={values.max_discount_amount}
                        isRequired
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) =>
                          setFieldValue("max_discount_amount", e.target.value.replace(/\D/g, ""))
                        }
                      />
                    </Grid>

                    {/* Row 3: Min Cart Value — Free Delivery — Delivery Fee Cap (only if checked) */}
                    <Grid item xs={12} md={4} sx={{ mt: 2 }}>
                      <InputField
                        label="Min Cart Value"
                        name="min_cart_value"
                        placeholder="Enter Min Cart Value"
                        value={values.min_cart_value}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) => setFieldValue("min_cart_value", e.target.value.replace(/\D/g, ""))}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 2, pl: { md: 2 }, display: "flex", alignItems: "center" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.free_delivery}
                            onChange={(e) => {
                              setFieldValue("free_delivery", e.target.checked);
                              if (!e.target.checked) {
                                setFieldValue("delivery_fee_cap", "");
                              }
                            }}
                          />
                        }
                        label="Free Delivery"
                      />
                    </Grid>
                    {/* Keep slot reserved so Select Store/Product stay on Row 4 */}
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{
                        mt: 2,
                        pl: { md: 2 },
                        visibility: values.free_delivery ? "visible" : "hidden",
                        pointerEvents: values.free_delivery ? "auto" : "none",
                      }}
                    >
                      <InputField
                        label="Delivery Fee Cap"
                        name="delivery_fee_cap"
                        placeholder="Enter Delivery Fee"
                        value={values.delivery_fee_cap}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) =>
                          setFieldValue("delivery_fee_cap", e.target.value.replace(/\D/g, ""))
                        }
                      />
                    </Grid>
                  </>
                ) : null}

                {showFreeDeliveryTypeFields ? (
                  <>
                    {/* Row 1: Prefix — Length */}
                    <Grid item xs={12} md={4} sx={{ mt: 1 }}>
                      <InputField
                        label="Prefix"
                        name="prefix"
                        placeholder="ITEM20"
                        value={values.prefix}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 1, pl: { md: 2 } }}>
                      <InputField
                        label="Length"
                        name="length"
                        placeholder="8"
                        value={values.length}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) => {
                          const onlyDigits = e.target.value.replace(/\D/g, "");
                          setFieldValue("length", onlyDigits);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 1, pl: { md: 2 } }}>
                      <InputField
                        label="Per User Limit"
                        name="user_usage_limit"
                        placeholder="eg: 1"
                        value={values.user_usage_limit}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) =>
                          setFieldValue("user_usage_limit", e.target.value.replace(/\D/g, ""))
                        }
                      />
                    </Grid>

                    {/* Row 2: Overall Usage Limit — Max Discount Value — Min Cart Value */}
                    <Grid item xs={12} md={4} sx={{ mt: 2 }}>
                      <InputField
                        label="Overall Usage Limit"
                        name="global_usage_limit"
                        placeholder="eg: 300"
                        value={values.global_usage_limit}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) =>
                          setFieldValue("global_usage_limit", e.target.value.replace(/\D/g, ""))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 2, pl: { md: 2 } }}>
                      <InputField
                        label="Max Discount Value"
                        name="max_discount_amount"
                        placeholder="Enter Max Discount Value"
                        value={values.max_discount_amount}
                        isRequired
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) =>
                          setFieldValue("max_discount_amount", e.target.value.replace(/\D/g, ""))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 2, pl: { md: 2 } }}>
                      <InputField
                        label="Min Cart Value"
                        name="min_cart_value"
                        placeholder="Enter Min Cart Value"
                        value={values.min_cart_value}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) =>
                          setFieldValue("min_cart_value", e.target.value.replace(/\D/g, ""))
                        }
                      />
                    </Grid>

                    {/* Row 3: Delivery Type — Delivery Fee Cap — empty column */}
                    <Grid item xs={12} md={4} sx={{ mt: 2 }}>
                      <SelectField
                        label="Delivery Type"
                        name="discount_type"
                        value={values.discount_type}
                        options={[
                          { label: "Flat", value: "rupees" },
                          { label: "Percent", value: "percent" },
                        ]}
                        isRequired
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{ mt: 2, pl: { md: 2 }, visibility: "hidden", pointerEvents: "none" }}
                    >
                      <Box sx={{ height: 40 }} />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{ mt: 2, pl: { md: 2 }, visibility: "hidden", pointerEvents: "none" }}
                    >
                      <Box sx={{ height: 40 }} />
                    </Grid>

                    {/* Row 4: Select Store — Select Product (2 columns only) */}
                    <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                      <SelectFieldWithSearch
                        label="Select Store"
                        name="store_reference_id"
                        value={values.store_reference_id}
                        options={storeOptions}
                        placeholder="Select store"
                        onChange={(e) => {
                          const selectedStoreId = (e.target as any)?.value as string;
                          fetchProductsForStore(String(selectedStoreId || ""));
                          setFieldValue("item_reference_ids", []);
                          setFieldValue("preorder_item_id", "");
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ mt: 2, pl: { md: 2 } }}>
                      <SelectFieldWithSearch
                        label="Select Product"
                        name="item_reference_ids"
                        value={values.item_reference_ids}
                        options={productOptions}
                        placeholder="Select product"
                        mode="tags"
                      />
                    </Grid>
                  </>
                ) : null}

                {showFirstOrderFields || showPreorderFields ? (
                  <>
                    {/* Row 1: Discount Type — Prefix — Length */}
                    <Grid item xs={12} md={4} sx={{ mt: 1 }}>
                      <SelectField
                        label="Discount Type"
                        name="discount_type"
                        value={values.discount_type}
                        options={[
                          { label: "Flat", value: "rupees" },
                          { label: "Percent", value: "percent" },
                        ]}
                        isRequired
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 1, pl: { md: 2 } }}>
                      <InputField
                        label="Prefix"
                        name="prefix"
                        placeholder="ITEM20"
                        value={values.prefix}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 1, pl: { md: 2 } }}>
                      <InputField
                        label="Length"
                        name="length"
                        placeholder="8"
                        value={values.length}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) => {
                          const onlyDigits = e.target.value.replace(/\D/g, "");
                          setFieldValue("length", onlyDigits);
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={4} sx={{ mt: 2 }}>
                      <InputField
                        label="Value"
                        name="value"
                        placeholder="20"
                        value={values.value}
                        isRequired
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) => setFieldValue("value", e.target.value.replace(/\D/g, ""))}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 2, pl: { md: 2 } }}>
                      <InputField
                        label="Per User Limit"
                        name="user_usage_limit"
                        placeholder="eg: 1"
                        value={values.user_usage_limit}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) =>
                          setFieldValue("user_usage_limit", e.target.value.replace(/\D/g, ""))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 2, pl: { md: 2 } }}>
                      <InputField
                        label="Overall Usage Limit"
                        name="global_usage_limit"
                        placeholder="eg: 300"
                        value={values.global_usage_limit}
                        isRequired={showPreorderFields}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) =>
                          setFieldValue("global_usage_limit", e.target.value.replace(/\D/g, ""))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 1 }}>
                      <InputField
                        label="Max Discount Value"
                        name="max_discount_amount"
                        placeholder="Enter Max Discount Value"
                        value={values.max_discount_amount}
                        isRequired
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) =>
                          setFieldValue("max_discount_amount", e.target.value.replace(/\D/g, ""))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 1, pl: { md: 2 } }}>
                      <InputField
                        label="Min Cart Value"
                        name="min_cart_value"
                        placeholder="Enter Min Cart Value"
                        value={values.min_cart_value}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onChange={(e) => setFieldValue("min_cart_value", e.target.value.replace(/\D/g, ""))}
                      />
                    </Grid>

                    {showFirstOrderFields ? (
                      <Grid item xs={12} md={4} sx={{ mt: 1, pl: { md: 2 } }}>
                        <InputField
                          label="Applicable Order #"
                          name="order_coupon_count"
                          placeholder="eg: 1st or nth"
                          value={values.order_coupon_count}
                          isRequired
                          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                          onChange={(e) =>
                            setFieldValue("order_coupon_count", e.target.value.replace(/\D/g, ""))
                          }
                        />
                      </Grid>
                    ) : null}

                    {showFirstOrderFields ? (
                      <>
                        {/* First Order: Free Delivery row above dates */}
                        <Grid item xs={12} md={4} sx={{ mt: 2, display: "flex", alignItems: "center" }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={values.free_delivery}
                                onChange={(e) => {
                                  setFieldValue("free_delivery", e.target.checked);
                                  if (!e.target.checked) {
                                    setFieldValue("delivery_fee_cap", "");
                                  }
                                }}
                              />
                            }
                            label="Free Delivery"
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={4}
                          sx={{
                            mt: 2,
                            pl: { md: 2 },
                            visibility: values.free_delivery ? "visible" : "hidden",
                            pointerEvents: values.free_delivery ? "auto" : "none",
                          }}
                        >
                          <InputField
                            label="Delivery Fee Cap"
                            name="delivery_fee_cap"
                            placeholder="Enter Delivery Fee Cap"
                            value={values.delivery_fee_cap}
                            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                            onChange={(e) =>
                              setFieldValue("delivery_fee_cap", e.target.value.replace(/\D/g, ""))
                            }
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={4}
                          sx={{ mt: 2, pl: { md: 2 }, visibility: "hidden", pointerEvents: "none" }}
                        >
                          <InputField label="-" name="__first_order_free_delivery_spacer" value="" />
                        </Grid>
                      </>
                    ) : null}

                    {showPreorderFields ? (
                      <Grid item xs={12} md={4} sx={{ mt: 1, pl: { md: 2 }, display: "flex", alignItems: "center" }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.free_delivery}
                              onChange={(e) => setFieldValue("free_delivery", e.target.checked)}
                            />
                          }
                          label="Free Delivery"
                        />
                      </Grid>
                    ) : null}
                  </>
                ) : null}

                {showPercentFields ? (
                  <>
                    <Grid item xs={12} md={4} sx={{ mt: 2 }}>
                      <SelectFieldWithSearch
                        label="Select Store"
                        name="store_reference_id"
                        value={values.store_reference_id}
                        options={storeOptions}
                        placeholder="Select store"
                        onChange={(e) => {
                          const selectedStoreId = (e.target as any)?.value as string;
                          fetchProductsForStore(String(selectedStoreId || ""));
                          setFieldValue("item_reference_ids", []);
                          setFieldValue("preorder_item_id", "");
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 2, pl: { md: 2 } }}>
                      <SelectFieldWithSearch
                        label="Select Products"
                        name="item_reference_ids"
                        value={values.item_reference_ids}
                        options={productOptions}
                        placeholder="Select products"
                        mode="tags"
                      />
                    </Grid>
                  </>
                ) : null}

                {showPreorderFields ? (
                  <>
                    {/* Preorder: Free Delivery -> Delivery Fee Cap in a new row (first column only), above store/product/title */}
                    {values.free_delivery ? (
                      <>
                        <Grid item xs={12} md={4} sx={{ mt: 2 }}>
                          <InputField
                            label="Delivery Fee Cap"
                            name="delivery_fee_cap"
                            placeholder="Enter Delivery Fee Cap"
                            value={values.delivery_fee_cap}
                            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                            onChange={(e) =>
                              setFieldValue("delivery_fee_cap", e.target.value.replace(/\D/g, ""))
                            }
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={4}
                          sx={{ mt: 2, pl: { md: 2 }, visibility: "hidden", pointerEvents: "none" }}
                        >
                          <InputField label="-" name="__preorder_delivery_fee_cap_spacer_1" value="" />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={4}
                          sx={{ mt: 2, pl: { md: 2 }, visibility: "hidden", pointerEvents: "none" }}
                        >
                          <InputField label="-" name="__preorder_delivery_fee_cap_spacer_2" value="" />
                        </Grid>
                      </>
                    ) : null}

                    <Grid item xs={12} md={4} sx={{ mt: 2 }}>
                      <SelectFieldWithSearch
                        label="Select Store"
                        name="store_reference_id"
                        value={values.store_reference_id}
                        options={storeOptions}
                        placeholder="Select store"
                        isRequired
                        onChange={(e) => {
                          const selectedStoreId = (e.target as any)?.value as string;
                          fetchProductsForStore(String(selectedStoreId || ""));
                          setFieldValue("item_reference_ids", []);
                          setFieldValue("preorder_item_id", "");
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 2, pl: { md: 2 } }}>
                      <SelectFieldWithSearch
                        label="Select Product"
                        name="preorder_item_id"
                        value={values.preorder_item_id}
                        options={productOptions}
                        placeholder="Select product"
                        isRequired
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ mt: 2, pl: { md: 2 } }}>
                      <InputField
                        label="Title"
                        name="preorder_title"
                        value={values.preorder_title}
                        placeholder="Enter Title"
                        inputBoxSize="small"
                      />
                    </Grid>
                  </>
                ) : null}

                <Grid item xs={12} md={showPreorderFields ? 4 : 6} sx={{ mt: 2 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Start Date"
                      value={values.start_at}
                      onChange={(newValue) => setFieldValue("start_at", newValue)}
                      maxDate={dayjs()}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: showFieldError("start_at"),
                          helperText: showFieldError("start_at") ? (errors as any)?.start_at : "",
                          sx: { width: "100%" },
                        },
                        openPickerButton: {
                          sx: {
                            border: "none",
                            boxShadow: "none",
                            "&:hover": { backgroundColor: "transparent" },
                          },
                        },
                      }}
                      format="YYYY-MM-DD"
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={showPreorderFields ? 4 : 6}
                  sx={{ mt: 2, pl: { md: 2 } }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Expiry Date"
                      value={values.expires_at}
                      onChange={(newValue) => setFieldValue("expires_at", newValue)}
                      minDate={dayjs()}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: showFieldError("expires_at"),
                          helperText: showFieldError("expires_at") ? (errors as any)?.expires_at : "",
                          required: values.coupon_type === "free_delivery",
                          sx: { width: "100%" },
                        },
                        openPickerButton: {
                          sx: {
                            border: "none",
                            boxShadow: "none",
                            "&:hover": { backgroundColor: "transparent" },
                          },
                        },
                      }}
                      format="YYYY-MM-DD"
                    />
                  </LocalizationProvider>
                </Grid>

                {showPreorderFields ? (
                  <Grid item xs={12} md={4} sx={{ mt: 2, pl: { md: 2 } }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Delivery Date"
                        value={values.preorder_delivery_date}
                        onChange={(newValue) => setFieldValue("preorder_delivery_date", newValue)}
                        minDate={dayjs()}
                        slotProps={{
                          textField: {
                            size: "small",
                            error: showFieldError("preorder_delivery_date"),
                            helperText: showFieldError("preorder_delivery_date")
                              ? (errors as any)?.preorder_delivery_date
                              : "",
                            sx: { width: "100%" },
                          },
                          openPickerButton: {
                            sx: {
                              border: "none",
                              boxShadow: "none",
                              "&:hover": { backgroundColor: "transparent" },
                            },
                          },
                        }}
                        format="YYYY-MM-DD"
                      />
                    </LocalizationProvider>
                  </Grid>
                ) : null}
              </Grid>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={isSubmitting} sx={{ ml: 2 }}>
                  Generate
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
            </Box>
          )}
        </Form>
        );
      }}
    </Formik>
  );
};

export default GenerateCouponsModal;

